/**
 * © 2026 Mimizuku & co.,Inc. All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, distribution, or use is strictly prohibited.
 */

/**
 * Display State Manager
 * 表示状態の管理とイベント検知ユーティリティ
 */

export type DisplayState = 'active' | 'protected' | 'hidden';

export interface DisplayStateCallbacks {
  onStateChange?: (state: DisplayState, reason: string) => void;
  onProtectionTriggered?: (reason: string) => void;
  onProtectionReleased?: () => void;
}

interface DisplayStateConfig {
  /** 保護モードの持続時間（ミリ秒） */
  protectionDuration?: number;
  /** フォーカス喪失時に保護を有効にするか */
  protectOnBlur?: boolean;
  /** visibility変更時に保護を有効にするか */
  protectOnVisibilityChange?: boolean;
  /** キーイベント検知を有効にするか */
  detectKeyEvents?: boolean;
  /** デバッグモード */
  debug?: boolean;
}

const DEFAULT_CONFIG: Required<DisplayStateConfig> = {
  protectionDuration: 500,
  protectOnBlur: true,
  protectOnVisibilityChange: true,
  detectKeyEvents: true,
  debug: false,
};

class DisplayStateManagerClass {
  private state: DisplayState = 'active';
  private callbacksMap: Map<string, DisplayStateCallbacks> = new Map();
  private config: Required<DisplayStateConfig> = DEFAULT_CONFIG;
  private protectionTimer: ReturnType<typeof setTimeout> | null = null;
  private isInitialized = false;
  private boundHandlers: {
    keydown?: (e: KeyboardEvent) => void;
    keyup?: (e: KeyboardEvent) => void;
    blur?: () => void;
    focus?: () => void;
    visibilitychange?: () => void;
    contextmenu?: (e: Event) => void;
    mousedown?: () => void;
    mouseup?: () => void;
    scroll?: () => void;
  } = {};
  private listenerIdCounter = 0;
  /** スクリーンショット選択モード（4キー押下後、マウス操作待ち） */
  private screenshotSelectionMode = false;
  /** マウスダウン検知済み */
  private mouseDownDetected = false;

  /**
   * 初期化（イベントリスナーのセットアップのみ）
   */
  init(_callbacks: DisplayStateCallbacks, config?: DisplayStateConfig): void {
    // 最初の呼び出しでイベントリスナーをセットアップ
    if (!this.isInitialized) {
      this.config = { ...DEFAULT_CONFIG, ...config };
      this.setupEventListeners();
      this.isInitialized = true;

      if (this.config.debug) {
        console.log('[DSM] Initialized with config:', this.config);
      }
    }

    // コールバックは登録しない（registerを使用）
  }

  /**
   * コールバックを登録（複数登録可能）
   * @returns リスナーID（解除時に使用）
   */
  register(callbacks: DisplayStateCallbacks): string {
    // 初期化されていなければ初期化
    if (!this.isInitialized) {
      this.init({});
    }

    const listenerId = `listener_${++this.listenerIdCounter}`;
    this.callbacksMap.set(listenerId, callbacks);

    if (this.config.debug) {
      console.log('[DSM] Registered listener:', listenerId, 'Total:', this.callbacksMap.size);
    }

    return listenerId;
  }

  /**
   * コールバックを解除
   */
  unregister(listenerId: string): void {
    this.callbacksMap.delete(listenerId);

    if (this.config.debug) {
      console.log('[DSM] Unregistered listener:', listenerId, 'Remaining:', this.callbacksMap.size);
    }
  }

  /**
   * イベントリスナーの設定
   */
  private setupEventListeners(): void {
    // キーイベント検知
    if (this.config.detectKeyEvents) {
      this.boundHandlers.keydown = this.handleKeyDown.bind(this);
      this.boundHandlers.keyup = this.handleKeyUp.bind(this);
      document.addEventListener('keydown', this.boundHandlers.keydown);
      document.addEventListener('keyup', this.boundHandlers.keyup);
    }

    // フォーカス検知
    if (this.config.protectOnBlur) {
      this.boundHandlers.blur = this.handleBlur.bind(this);
      this.boundHandlers.focus = this.handleFocus.bind(this);
      window.addEventListener('blur', this.boundHandlers.blur);
      window.addEventListener('focus', this.boundHandlers.focus);
    }

    // Visibility変更検知
    if (this.config.protectOnVisibilityChange) {
      this.boundHandlers.visibilitychange = this.handleVisibilityChange.bind(this);
      document.addEventListener('visibilitychange', this.boundHandlers.visibilitychange);
    }

    // 右クリック検知（オプション）
    this.boundHandlers.contextmenu = this.handleContextMenu.bind(this);
    document.addEventListener('contextmenu', this.boundHandlers.contextmenu);

    // マウスイベント検知（スクリーンショット選択モード用）
    this.boundHandlers.mousedown = this.handleMouseDown.bind(this);
    this.boundHandlers.mouseup = this.handleMouseUp.bind(this);
    window.addEventListener('mousedown', this.boundHandlers.mousedown);
    window.addEventListener('mouseup', this.boundHandlers.mouseup);

    // スクロール検知（フォーカスなしでも発火）
    this.boundHandlers.scroll = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.boundHandlers.scroll, true);
  }

  /**
   * キーダウンイベントハンドラ
   */
  private handleKeyDown(e: KeyboardEvent): void {
    // PrintScreen
    if (e.key === 'PrintScreen') {
      this.triggerProtection('key_printscreen');
      return;
    }

    // Mac: Cmd+Shift (スクリーンショット)
    // OSが4キーを先に処理するため、Cmd+Shiftの時点で選択モードに入る
    if (e.metaKey && e.shiftKey) {
      this.screenshotSelectionMode = true;
      this.mouseDownDetected = false;
      if (this.config.debug) {
        console.log('[DSM] Screenshot selection mode activated (Cmd+Shift)');
      }
      this.triggerProtection('key_mac_screenshot');
      return;
    }

    // Windows: Win+Shift+S (Snipping Tool)
    if (e.metaKey && e.shiftKey && e.key.toLowerCase() === 's') {
      this.triggerProtection('key_win_snip');
      return;
    }

    // Windows: Win+PrintScreen
    if (e.metaKey && e.key === 'PrintScreen') {
      this.triggerProtection('key_win_printscreen');
      return;
    }

    // Ctrl+P (印刷)
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p') {
      this.triggerProtection('key_print');
      e.preventDefault();
      return;
    }

    // F12 (DevTools) - オプション
    if (e.key === 'F12') {
      this.triggerProtection('key_devtools');
      return;
    }

    // Ctrl+Shift+I (DevTools)
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'i') {
      this.triggerProtection('key_devtools_shortcut');
      return;
    }
  }

  /**
   * キーアップイベントハンドラ
   */
  private handleKeyUp(e: KeyboardEvent): void {
    // Cmd+Shiftが解除されたら保護解除
    if (this.screenshotSelectionMode && (e.key === 'Meta' || e.key === 'Shift')) {
      if (this.config.debug) {
        console.log('[DSM] Cmd+Shift released, releasing protection');
      }
      this.screenshotSelectionMode = false;
      this.mouseDownDetected = false;
      this.releaseProtection();
    }

    // Escapeキーで保護解除（スクリーンショットキャンセル時など）
    if (e.key === 'Escape' && this.state === 'protected') {
      if (this.config.debug) {
        console.log('[DSM] Escape pressed, releasing protection');
      }
      this.screenshotSelectionMode = false;
      this.mouseDownDetected = false;
      this.releaseProtection();
    }
  }

  /**
   * フォーカス喪失ハンドラ
   */
  private handleBlur(): void {
    this.triggerProtection('window_blur');
  }

  /**
   * フォーカス取得ハンドラ
   */
  private handleFocus(): void {
    // スクリーンショット選択中（マウスダウン検知済み）はフォーカス復帰でも解除しない
    if (this.screenshotSelectionMode && this.mouseDownDetected) {
      if (this.config.debug) {
        console.log('[DSM] Focus returned but screenshot selection in progress, keeping protection');
      }
      return;
    }
    // マウスダウン前ならフォーカス復帰で解除（Cmd+Shift+3など）
    this.screenshotSelectionMode = false;
    this.releaseProtection();
  }

  /**
   * Visibility変更ハンドラ
   */
  private handleVisibilityChange(): void {
    if (document.hidden) {
      this.triggerProtection('visibility_hidden');
    } else {
      // スクリーンショット選択中（マウスダウン検知済み）はvisibility復帰でも解除しない
      if (this.screenshotSelectionMode && this.mouseDownDetected) {
        if (this.config.debug) {
          console.log('[DSM] Visibility restored but screenshot selection in progress, keeping protection');
        }
        return;
      }
      this.screenshotSelectionMode = false;
      this.releaseProtection();
    }
  }

  /**
   * 右クリックハンドラ
   */
  private handleContextMenu(e: Event): void {
    // 画像要素での右クリックを検知
    const target = e.target as HTMLElement;
    if (target.tagName === 'CANVAS' || target.tagName === 'IMG') {
      this.triggerProtection('contextmenu');
    }
  }

  /**
   * マウスダウンハンドラ（スクリーンショット選択モード用）
   */
  private handleMouseDown(): void {
    if (this.screenshotSelectionMode) {
      this.mouseDownDetected = true;
      if (this.config.debug) {
        console.log('[DSM] Mouse down detected in screenshot selection mode');
      }
    }
  }

  /**
   * マウスアップハンドラ（スクリーンショット選択モード用）
   */
  private handleMouseUp(): void {
    if (this.screenshotSelectionMode && this.mouseDownDetected) {
      if (this.config.debug) {
        console.log('[DSM] Mouse up detected, releasing');
      }
      this.screenshotSelectionMode = false;
      this.mouseDownDetected = false;
      this.releaseProtection();
    }
  }

  /**
   * スクロールハンドラ（フォーカスなしでも復帰可能）
   */
  private handleScroll(): void {
    if (this.state === 'protected' && !this.screenshotSelectionMode) {
      if (this.config.debug) {
        console.log('[DSM] Scroll detected, releasing protection');
      }
      this.releaseProtection();
    }
  }

  /**
   * 保護モードを発動
   */
  triggerProtection(reason: string): void {
    if (this.config.debug) {
      console.log('[DSM] Protection triggered:', reason, 'Notifying', this.callbacksMap.size, 'listeners');
    }

    this.state = 'protected';

    // 全てのリスナーに通知
    this.callbacksMap.forEach((callbacks, id) => {
      try {
        callbacks.onStateChange?.('protected', reason);
        callbacks.onProtectionTriggered?.(reason);
      } catch (e) {
        console.error('[DSM] Error in callback', id, e);
      }
    });

    // タイマーリセット
    if (this.protectionTimer) {
      clearTimeout(this.protectionTimer);
    }

    // 一定時間後に自動解除（スクリーンショット選択モード中は解除しない）
    this.protectionTimer = setTimeout(() => {
      if (this.state === 'protected') {
        // スクリーンショット選択モード中は解除しない（マウス操作完了を待つ）
        if (this.screenshotSelectionMode) {
          if (this.config.debug) {
            console.log('[DSM] Protection timer expired but screenshot selection mode active, keeping protection');
          }
          return;
        }
        // フォーカスがない場合も解除しない
        if (!document.hasFocus()) {
          if (this.config.debug) {
            console.log('[DSM] Protection timer expired but window not focused, keeping protection');
          }
          return;
        }
        this.releaseProtection();
      }
    }, this.config.protectionDuration);
  }

  /**
   * 保護モードを解除
   */
  releaseProtection(): void {
    if (this.state !== 'protected') return;

    if (this.config.debug) {
      console.log('[DSM] Protection released, Notifying', this.callbacksMap.size, 'listeners');
    }

    this.state = 'active';

    // 全てのリスナーに通知
    this.callbacksMap.forEach((callbacks, id) => {
      try {
        callbacks.onStateChange?.('active', 'released');
        callbacks.onProtectionReleased?.();
      } catch (e) {
        console.error('[DSM] Error in callback', id, e);
      }
    });

    if (this.protectionTimer) {
      clearTimeout(this.protectionTimer);
      this.protectionTimer = null;
    }
  }

  /**
   * 現在の状態を取得
   */
  getState(): DisplayState {
    return this.state;
  }

  /**
   * 手動で保護モードを設定
   */
  setProtected(duration?: number): void {
    this.triggerProtection('manual');

    if (duration) {
      if (this.protectionTimer) {
        clearTimeout(this.protectionTimer);
      }
      this.protectionTimer = setTimeout(() => {
        this.releaseProtection();
      }, duration);
    }
  }

  /**
   * クリーンアップ
   */
  cleanup(): void {
    if (this.boundHandlers.keydown) {
      document.removeEventListener('keydown', this.boundHandlers.keydown);
    }
    if (this.boundHandlers.keyup) {
      document.removeEventListener('keyup', this.boundHandlers.keyup);
    }
    if (this.boundHandlers.blur) {
      window.removeEventListener('blur', this.boundHandlers.blur);
    }
    if (this.boundHandlers.focus) {
      window.removeEventListener('focus', this.boundHandlers.focus);
    }
    if (this.boundHandlers.visibilitychange) {
      document.removeEventListener('visibilitychange', this.boundHandlers.visibilitychange);
    }
    if (this.boundHandlers.contextmenu) {
      document.removeEventListener('contextmenu', this.boundHandlers.contextmenu);
    }
    if (this.boundHandlers.mousedown) {
      window.removeEventListener('mousedown', this.boundHandlers.mousedown);
    }
    if (this.boundHandlers.mouseup) {
      window.removeEventListener('mouseup', this.boundHandlers.mouseup);
    }
    if (this.boundHandlers.scroll) {
      window.removeEventListener('scroll', this.boundHandlers.scroll, true);
    }

    if (this.protectionTimer) {
      clearTimeout(this.protectionTimer);
    }

    this.boundHandlers = {};
    this.isInitialized = false;
    this.state = 'active';
    this.screenshotSelectionMode = false;
    this.mouseDownDetected = false;

    if (this.config.debug) {
      console.log('[DSM] Cleaned up');
    }
  }
}

// シングルトンインスタンス
export const displayStateManager = new DisplayStateManagerClass();

/**
 * React Hook用のヘルパー
 */
export function createDisplayStateHook() {
  return {
    init: displayStateManager.init.bind(displayStateManager),
    register: displayStateManager.register.bind(displayStateManager),
    unregister: displayStateManager.unregister.bind(displayStateManager),
    cleanup: displayStateManager.cleanup.bind(displayStateManager),
    getState: displayStateManager.getState.bind(displayStateManager),
    setProtected: displayStateManager.setProtected.bind(displayStateManager),
    triggerProtection: displayStateManager.triggerProtection.bind(displayStateManager),
    releaseProtection: displayStateManager.releaseProtection.bind(displayStateManager),
  };
}
